import type { SequenceRead } from './sequenceHead.ts'
import type { KnownPrimerPair } from './primerRegistry.ts'
import { asPrimerPair } from './primerRegistry.ts'
import type { PrimerTrimOptions } from './primerTrimDetection.ts'
import { DEFAULT_PRIMER_TRIM_OPTIONS, primerStartRates } from './primerTrimDetection.ts'

export type PrimerPairMatch = {
  pair: KnownPrimerPair
  /** Share of primer-bearing reads for this pair. */
  rate: number
  /** `shortlist` when a candidate settled it, `registry` when the whole registry had to be scanned. */
  via: 'shortlist' | 'registry'
}

/** A pair "opens" a file when at least this share of its reads bears one of its primers. */
export const PAIR_FOUND_AT_LEAST = 0.5
/** Reads a full-registry scan screens every pair on before spending the rest of the head on survivors. */
const SCREEN_READS = 40
const SCREEN_KEEPS_AT_LEAST = 0.2

/** Pairs within this rate of the best compete on match quality instead. */
const RATE_TIE = 0.05
/* a merged or single-end read counts for a pair by one end alone, so a pair sharing only one primer with the reads ties on rate */
const BOTH_PRIMERS_AT_LEAST = 0.1

const rateOf = (reads: readonly SequenceRead[], pair: KnownPrimerPair, options: PrimerTrimOptions): number =>
  primerStartRates(reads, asPrimerPair(pair), options)?.primerFoundRate ?? 0

type Scored = { pair: KnownPrimerPair; rate: number; errors: number; both: boolean }

/** Every pair within {@link RATE_TIE} of the best, ranked; empty when no pair matches a read. */
const ranked = (reads: readonly SequenceRead[], pairs: readonly KnownPrimerPair[], options: PrimerTrimOptions): Scored[] => {
  const scored: Scored[] = pairs.map((pair) => {
    const rates = primerStartRates(reads, asPrimerPair(pair), options)
    return {
      pair,
      rate: rates?.primerFoundRate ?? 0,
      errors: rates?.matchErrorRate ?? 1,
      both: (rates?.weakerPrimerRate ?? 0) >= BOTH_PRIMERS_AT_LEAST
    }
  })
  const top = Math.max(0, ...scored.map((s) => s.rate))
  const contenders = scored.filter((s) => s.rate >= top - RATE_TIE && s.rate > 0)
  /* both primers on the reads first, then fewest edits, then the longer primers, which are the less likely to match by accident */
  return contenders.sort(
    (a, b) =>
      Number(b.both) - Number(a.both) ||
      a.errors - b.errors ||
      b.pair.first.length + b.pair.second.length - (a.pair.first.length + a.pair.second.length)
  )
}

/**
 * `match`: the known pair whose primers the file's reads carry. `nearest`: without a match, the pair that
 * came closest, so a pair on some reads only is not mistaken for primers removed. `oneSided`: without a
 * match, the pairs sharing the one primer the reads show — the partner is not on them, so which pair it
 * is stays open.
 */
export type PrimerPairSearch = { match: PrimerPairMatch | null; nearest: KnownPrimerPair | null; oneSided?: KnownPrimerPair[] }

export type PrimerPairSearchOptions = {
  /** Pairs identified on other files of the batch; one of them settles a one-sided file. */
  found?: readonly KnownPrimerPair[]
  /**
   * The reads are raw single mates (an R1 without its R2): the partner primer is beyond their end, so no
   * pair is settled by them alone.
   */
  oneMate?: boolean
}

/**
 * Evidence always comes from a single pair — primers of two different registrations are never combined.
 * The shortlist (the order's entered pair, pairs registered for its target, pairs already found on sibling
 * files) is tried first; only when none of it opens the file with both primers is the whole registry
 * scanned, screening every pair on a few reads first. A pair opening the file by one primer is taken only
 * when no other pair carries that primer too.
 */
export const searchPrimerPair = (
  reads: readonly SequenceRead[],
  registry: readonly KnownPrimerPair[],
  shortlist: readonly KnownPrimerPair[] = [],
  options: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS,
  search: PrimerPairSearchOptions = {}
): PrimerPairSearch => {
  const opens = (s: Scored | undefined): s is Scored => !!s && s.rate >= PAIR_FOUND_AT_LEAST
  const settled = (s: Scored | undefined): s is Scored => opens(s) && s.both && !search.oneMate
  const fromShortlist = ranked(reads, shortlist, options)
  if (settled(fromShortlist[0])) {
    return { match: { pair: fromShortlist[0].pair, rate: fromShortlist[0].rate, via: 'shortlist' }, nearest: null }
  }
  const tried = new Set(shortlist.map((pair) => pair.id))
  const screenReads = reads.slice(0, SCREEN_READS)
  const screened = registry.filter((pair) => !tried.has(pair.id)).map((pair) => ({ pair, rate: rateOf(screenReads, pair, options) }))
  const fromRegistry = ranked(
    reads,
    screened.filter((s) => s.rate >= SCREEN_KEEPS_AT_LEAST).map((s) => s.pair),
    options
  )
  if (settled(fromRegistry[0])) {
    return { match: { pair: fromRegistry[0].pair, rate: fromRegistry[0].rate, via: 'registry' }, nearest: null }
  }
  const top = Math.max(0, ...[...fromShortlist, ...fromRegistry].map((s) => s.rate))
  const sharing = [...fromShortlist, ...fromRegistry].filter((s) => opens(s) && s.rate >= top - RATE_TIE)
  if (sharing.length > 0) {
    const found = sharing.filter((s) => search.found?.some((pair) => pair.id === s.pair.id))
    const only = found.length === 1 ? found[0] : sharing.length === 1 && !search.oneMate ? sharing[0] : undefined
    if (only) {
      return { match: { pair: only.pair, rate: only.rate, via: fromShortlist.includes(only) ? 'shortlist' : 'registry' }, nearest: null }
    }
    return { match: null, nearest: null, oneSided: sharing.map((s) => s.pair) }
  }
  const candidates = [fromShortlist[0], fromRegistry[0], ...screened].flatMap((s) => (s && s.rate > 0 ? [s] : []))
  const nearest = candidates.reduce<(typeof candidates)[number] | null>((a, b) => (!a || b.rate > a.rate ? b : a), null)
  return { match: null, nearest: nearest?.pair ?? null }
}

/** The one known pair whose primers the file's reads carry, or `null`; see {@link searchPrimerPair}. */
export const identifyPrimerPair = (
  reads: readonly SequenceRead[],
  registry: readonly KnownPrimerPair[],
  shortlist: readonly KnownPrimerPair[] = [],
  options: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS
): PrimerPairMatch | null => searchPrimerPair(reads, registry, shortlist, options).match

/**
 * {@link searchPrimerPair} for every file of a batch. A pair found on one file joins the shortlist for
 * the rest, so a batch on one pair scans the registry once while a mixed batch still gets each file's own.
 */
export const identifyPrimerPairs = (
  files: readonly (readonly SequenceRead[])[],
  registry: readonly KnownPrimerPair[],
  shortlist: readonly KnownPrimerPair[] = [],
  options: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS,
  oneMate: (index: number) => boolean = () => false
): PrimerPairSearch[] => {
  const candidates = [...shortlist]
  const found: KnownPrimerPair[] = []
  return files.map((reads, i) => {
    const search = searchPrimerPair(reads, registry, candidates, options, { found, oneMate: oneMate(i) })
    const match = search.match
    if (match && !candidates.some((pair) => pair.id === match.pair.id)) {
      candidates.unshift(match.pair)
    }
    if (match && !found.some((pair) => pair.id === match.pair.id)) {
      found.push(match.pair)
    }
    return search
  })
}
